import { httpClient } from '../client';
import { logger } from '../../../utils/logger';
import { ApiErrorHandler } from '../error.handler';
import { BaseEndpoint } from './base.endpoint';

export interface Dealer {
  cognitoId: string;
  email: string;
  firstName: string;
  lastName?: string;
  mobileNo?: string;
  promoCode: string;
  createdAt?: string;
}

export interface CreateDealerDTO {
  email: string;
  firstName: string;
  lastName?: string;
  mobileNo?: string;
  promoCode: string;
}

class DealerEndpoints extends BaseEndpoint {
  private static instance: DealerEndpoints;

  private constructor() {
    super();
  }

  static getInstance(): DealerEndpoints {
    if (!DealerEndpoints.instance) {
      DealerEndpoints.instance = new DealerEndpoints();
    }
    return DealerEndpoints.instance;
  }

  /**
   * Create a new dealer admin user
   * Calls backend endpoint which uses AdminCreateUser
   * Dealer will receive instructions to set password via "Forgot Password"
   */
  async createDealer(data: CreateDealerDTO): Promise<Dealer> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.post<{ message: string; data: Dealer }>(
        `${this.baseUrl}/dealers`,
        data,
        { headers }
      );

      logger.info('Dealer created successfully');
      return response.data.data;
    } catch (error: any) {
      logger.error('Failed to create dealer:', error);
      // Extract error message from response if available
      const message = error?.response?.data?.message || error?.message || 'Failed to create dealer';
      throw new Error(message);
    }
  }

  /**
   * Get all dealer users
   * Fetches dealers directly from Cognito via backend
   */
  async getDealers(): Promise<Dealer[]> {
    try {
      const headers = await this.getAuthHeaders();
      const response = await httpClient.get<{ data: Dealer[] }>(`${this.baseUrl}/dealers`, {
        headers,
      });

      logger.info('Dealers fetched successfully');
      return response.data.data || [];
    } catch (error) {
      logger.warn('Failed to fetch dealers:', error);
      return [];
    }
  }

  /**
   * Delete a dealer user
   */
  async deleteDealer(cognitoId: string): Promise<void> {
    try {
      const headers = await this.getAuthHeaders();
      await httpClient.delete(`${this.baseUrl}/dealers/${encodeURIComponent(cognitoId)}`, {
        headers,
      });

      logger.info('Dealer deleted successfully');
    } catch (error) {
      logger.error('Failed to delete dealer:', error);
      return ApiErrorHandler.handle(error);
    }
  }
}

export const dealerEndpoints = DealerEndpoints.getInstance();
